import React from 'react'
import { toMoney } from '@mobi/utils/money'
import { Banner } from '@mobi/component-library/Feedback/Banner'
import { RadioButton } from '@mobi/component-library/Selection&Input/RadioButton'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { spacing } from '@mobi/component-library/Foundations'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import { BetCardTitle } from '@mobi/betslip/Components/Common/BetCardTitle'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import { KambiHistoryCouponDisplayContext } from '@core/Areas/KambiSports/Player/Components/Coupon/context'
import { CountdownStatus } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/Common/CountdownStatus'
import { LegsProgressStatus } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/Common/LegsProgressStatus'
import { SameGameMulti } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/BetInfo/Components/SameGameMulti'
import { Single } from '@core/Areas/KambiSports/Player/Components/Coupon/Components/BetInfo/Components/Single'
import { getLegsProgressOutcomes } from '@core/Areas/KambiSports/Player/helpers/getLegsProgressOutcomes'

export const Multi: React.FC<{
  historyCoupon: BetLoopKambiShareCoupon | KambiHistoryCoupon
}> = ({ historyCoupon }) => {
  const instanceId = React.useId()
  const [isForcedPendingStatus, setIsForcedPendingStatus] = React.useReducer(() => true, false)
  const couponDisplayContext = React.useContext(KambiHistoryCouponDisplayContext)

  const isBetLoopQuickShare = couponDisplayContext.type === 'betloop-quick-share'
  const isExternalShare = couponDisplayContext.type === 'external-share'
  const isBetHistoryCoupon = couponDisplayContext.type === 'bet-history'

  const legsCount = historyCoupon.bets[0].couponRowIndexes.length
  const multiTitleText = `${legsCount} Leg Multi`
  const multiCombinedReturn = toMoney(
    formatKambiCurrencyToDollarDecimal(historyCoupon.bets[0].betOdds)
  )

  const legsProgressOutcomes = getLegsProgressOutcomes(historyCoupon)

  const isBettable = historyCoupon.outcomes.every(outcome => outcome.status === 'OPEN')

  const couponRows = historyCoupon.couponRows.filter(({ index }) =>
    historyCoupon.bets[0].couponRowIndexes.includes(index)
  )

  return (
    <>
      {!isBetHistoryCoupon && (
        <BetCardTitle
          title={multiTitleText}
          rightComponent={
            <Flex flexDirection='column' alignItems='flex-end' gap='0.2rem'>
              <Flex gap={spacing.smx2}>
                {multiCombinedReturn}

                {isBetLoopQuickShare && (
                  <RadioButton
                    size='md'
                    type='Tick'
                    id={instanceId}
                    isChecked={couponDisplayContext.data.isSelected}
                  />
                )}
              </Flex>

              {isForcedPendingStatus ? (
                <StatusBadge badgeSize='S' color='neutral'>
                  Pending
                </StatusBadge>
              ) : (
                (!isBettable || isExternalShare) && (
                  <CountdownStatus
                    displayType='multi-status'
                    betStatus={historyCoupon.bets[0].betStatus}
                    eventStartTime={null}
                  />
                )
              )}
            </Flex>
          }
          bottomLeftComponent={
            <LegsProgressStatus
              outcomes={legsProgressOutcomes}
              betOffers={historyCoupon.betOffers}
              events={historyCoupon.events}
            />
          }
        />
      )}

      {couponRows.map(couponRow => {
        if (couponRow.selectionType === 'BET_BUILDER' && couponRow.outcomeGroup) {
          const sameGameMultiOutcomeIds = couponRow.outcomeGroup.groups.flatMap(
            group => group.outcomeIds
          )
          const outcomes = historyCoupon.outcomes.filter(outcome =>
            sameGameMultiOutcomeIds.includes(outcome.outcomeId)
          )
          const events = historyCoupon.events.filter(event => event.eventId === outcomes[0].eventId)

          return (
            <BetCard.InnerContainerStyled key={couponRow.index}>
              <SameGameMulti
                isWithinMultiBet
                onMultiLegCountdownReachingZero={setIsForcedPendingStatus}
                legCount={outcomes.length}
                event={events[0]}
                outcomes={outcomes}
                betOffers={historyCoupon.betOffers}
                betStatus={couponRow.status}
                playedOdds={couponRow.oddsBoosted ?? couponRow.playedOdds}
              />
            </BetCard.InnerContainerStyled>
          )
        }

        const outcome = historyCoupon.outcomes.find(o => o.outcomeId === couponRow.outcomeId)

        if (!outcome) {
          return !PRODUCTION ? (
            <Banner key={couponRow.index} text='Unable to match coupon' type='neutral' />
          ) : null
        }

        const event = historyCoupon.events.find(e => e.eventId === outcome.eventId)
        const betOffer = historyCoupon.betOffers.find(bo => bo.betOfferId === outcome.betOfferId)

        if (!event || !betOffer) {
          return !PRODUCTION ? (
            <Banner key={couponRow.index} text='Unable to match event / bet offer' type='neutral' />
          ) : null
        }

        return (
          <BetCard.InnerContainerStyled key={outcome.outcomeId}>
            <Single
              isWithinMultiBet
              onMultiLegCountdownReachingZero={setIsForcedPendingStatus}
              event={event}
              outcome={outcome}
              betOffers={[betOffer]}
              betStatus={couponRow.status}
              playedOdds={couponRow?.playedOdds ? couponRow.playedOdds : undefined}
            />
          </BetCard.InnerContainerStyled>
        )
      })}
    </>
  )
}
